import { useEffect, useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import Button from '../../ui/Button';
import nurseryService from '../../../services/nursery';
import { errorMessage } from '../../../utils/apiResponse';
import { getTodayInTz } from '../../../utils/timezone';
import { addDays } from '../../../utils/payPeriod';
import { cn } from '../../../utils/cn';
import { despatchWhen } from './despatchText';
import {
  DESPATCH_METHOD_LABELS,
  type DespatchOrder,
  type NurseryLoad,
  type NurseryLoadType,
} from '../../../types/nursery';

/**
 * Put more customers on a load from the load's own page, including once it has
 * gone to the pickers. Lists every open order on no load, by customer; ticking
 * a customer takes all of their orders.
 *
 * Read from the planner, which already shows overdue and undated orders in any
 * window, so a long window here only adds orders due later.
 */

const WINDOW_DAYS = 120;

interface CustomerGroup {
  key: string;
  name: string;
  orders: DespatchOrder[];
  trees: number;
  matches: boolean;
}

export interface AddCustomersDialogProps {
  load: NurseryLoad;
  timezone: string;
  onClose: () => void;
  onAdded: (load: NurseryLoad) => void;
}

/** Whether an order goes the way this load does. An ad hoc load takes anything. */
function fitsLoad(o: DespatchOrder, type: NurseryLoadType): boolean {
  return type === 'ad_hoc' || o.despatch_method === type;
}

export function AddCustomersDialog({ load, timezone, onClose, onAdded }: AddCustomersDialogProps) {
  const [orders, setOrders] = useState<DespatchOrder[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const from = getTodayInTz(timezone);
    nurseryService.getDespatchPlanner(from, addDays(from, WINDOW_DAYS))
      .then((res) => {
        if (res.success) setOrders(res.data.orders.filter((o) => !o.load_id));
        else setError(errorMessage(res, 'Could not read the open orders'));
      })
      .catch((e) => setError(errorMessage(e, 'Could not read the open orders')));
  }, [timezone]);

  const groups = useMemo<CustomerGroup[]>(() => {
    const map = new Map<string, CustomerGroup>();
    for (const o of orders ?? []) {
      const key = o.customer_id != null ? `c:${o.customer_id}` : `n:${o.customer_name ?? ''}`;
      if (!map.has(key)) map.set(key, { key, name: o.customer_name || 'No customer', orders: [], trees: 0, matches: false });
      const g = map.get(key)!;
      g.orders.push(o);
      g.trees += o.total_trees;
      if (fitsLoad(o, load.load_type)) g.matches = true;
    }
    const q = query.trim().toLowerCase();
    return [...map.values()]
      .filter((g) => !q || g.name.toLowerCase().includes(q)
        || g.orders.some((o) => o.order_number.toLowerCase().includes(q) || (o.customer_code ?? '').toLowerCase().includes(q)))
      .sort((a, b) => Number(b.matches) - Number(a.matches) || a.name.localeCompare(b.name));
  }, [orders, query, load.load_type]);

  const toggle = (ids: number[], on: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  };

  const chosen = (orders ?? []).filter((o) => selected.has(o.id));
  const chosenTrees = chosen.reduce((n, o) => n + o.total_trees, 0);
  const chosenCustomers = new Set(chosen.map((o) => (o.customer_id != null ? `c:${o.customer_id}` : `n:${o.customer_name ?? ''}`))).size;
  const picking = load.status !== 'planning';

  const add = async () => {
    if (!chosen.length) return;
    setSaving(true);
    setError(null);
    try {
      const res = await nurseryService.addOrdersToLoad(load.id, chosen.map((o) => o.id));
      if (res.success) onAdded(res.data);
      else setError(errorMessage(res, 'Could not add those orders'));
    } catch (e) {
      setError(errorMessage(e, 'Could not add those orders'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true" aria-labelledby="add-customers-title">
      <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-xl shadow-xl max-h-[92vh] flex flex-col">
        <div className="flex items-start justify-between gap-3 px-5 pt-5">
          <div className="min-w-0">
            <h2 id="add-customers-title" className="text-lg font-semibold text-neutral-900">Add customers to {load.load_number}</h2>
            <p className="text-sm text-neutral-500">
              {picking
                ? 'The pickers see them on the pick list straight away.'
                : 'Open orders on no load, by customer.'}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 rounded hover:bg-neutral-100 flex-shrink-0">
            <X className="h-5 w-5 text-neutral-500" />
          </button>
        </div>

        <div className="px-5 pt-3">
          <label className="relative block">
            <span className="sr-only">Find a customer</span>
            <Search className="h-4 w-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Customer, store or order number"
              className="w-full rounded-lg border border-neutral-300 pl-9 pr-3 py-2 text-sm"
            />
          </label>
        </div>

        <div className="px-5 py-3 overflow-y-auto flex-1 min-h-0">
          {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 mb-3">{error}</div>}
          {orders === null && !error ? (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => <div key={i} className="h-12 bg-neutral-100 rounded animate-pulse" />)}
            </div>
          ) : groups.length === 0 ? (
            <p className="text-sm text-neutral-500 text-center py-6">
              {query ? 'No customer matches that.' : 'Every open order is already on a load.'}
            </p>
          ) : (
            <ul className="space-y-2" aria-label="Customers with orders on no load">
              {groups.map((g) => {
                const ids = g.orders.map((o) => o.id);
                const ticked = ids.filter((id) => selected.has(id)).length;
                return (
                  <li key={g.key} className={cn('rounded-lg border', ticked ? 'border-primary-300 bg-primary-50/40' : 'border-neutral-200')}>
                    <label className="flex items-start gap-3 px-3 py-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={ticked === ids.length}
                        ref={(el) => { if (el) el.indeterminate = ticked > 0 && ticked < ids.length; }}
                        onChange={(e) => toggle(ids, e.target.checked)}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block font-medium text-neutral-900 break-words">{g.name}</span>
                        <span className="block text-xs text-neutral-500">
                          {g.orders.length} order{g.orders.length === 1 ? '' : 's'}, {g.trees.toLocaleString()} trees
                          {!g.matches && ' · despatched another way'}
                        </span>
                      </span>
                    </label>
                    {g.orders.length > 1 && (
                      <ul className="border-t border-neutral-100 px-3 py-1">
                        {g.orders.map((o) => {
                          const when = despatchWhen(o, timezone);
                          return (
                            <li key={o.id}>
                              <label className="flex items-start gap-3 py-1 pl-6 cursor-pointer text-sm">
                                <input type="checkbox" className="mt-1" checked={selected.has(o.id)}
                                  onChange={(e) => toggle([o.id], e.target.checked)} />
                                <span className="min-w-0 flex-1">
                                  <span className="text-neutral-800">{o.order_number}</span>
                                  <span className="block text-xs text-neutral-500 break-words">
                                    {[DESPATCH_METHOD_LABELS[o.despatch_method], when, `${o.total_trees.toLocaleString()} trees`].filter(Boolean).join(' · ')}
                                  </span>
                                </span>
                              </label>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                    {g.orders.length === 1 && (
                      <p className="px-3 pb-2 pl-10 text-xs text-neutral-500 break-words">
                        {[g.orders[0].order_number, DESPATCH_METHOD_LABELS[g.orders[0].despatch_method], despatchWhen(g.orders[0], timezone)].filter(Boolean).join(' · ')}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 px-5 py-4 border-t border-neutral-100">
          <span className="text-sm text-neutral-600 mr-auto">
            {chosen.length
              ? `${chosenCustomers} customer${chosenCustomers === 1 ? '' : 's'}, ${chosenTrees.toLocaleString()} trees`
              : 'None chosen'}
          </span>
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button size="sm" onClick={add} disabled={saving || !chosen.length}>{saving ? 'Adding…' : 'Add to load'}</Button>
        </div>
      </div>
    </div>
  );
}

export default AddCustomersDialog;
